/**
 * ViewPermissionsContext.js — per-view Add / Edit / Delete flags
 *
 * Every node of POST api/ViewManagment/GetViewsByUserId carries canAdd,
 * canEdit and canDelete. The backend enforces them (403 on a blocked write);
 * this context only mirrors them so pages can hide the matching buttons.
 *
 * Rules (backend handoff 2026-10-01):
 *   - A missing flag counts as true.
 *   - Flags on group nodes have no effect — only leaf pages are mapped, so
 *     the "Vehicles" group can never hide the `vehicles` page.
 *   - A user with no node for a view is not restricted.
 *
 * App.js owns the views tree and feeds the flattened list in here.
 *
 * Usage:
 *   const { canAdd, canEdit, canDelete } = useViewPermissions('zones');
 */

import React, { createContext, useContext, useMemo } from 'react';

/**
 * View records store their key in `url`, sometimes with a leading slash and
 * mixed case. Normalising once keeps lookups (routing and permissions) stable.
 */
export const normalizeViewKey = (node) =>
  String(node?.url || node?.name || '')
    .toLowerCase()
    .trim()
    .replace(/^\/+/, '');

const FULL_ACCESS = Object.freeze({ canAdd: true, canEdit: true, canDelete: true });

/** Flattened views list → Map of leaf view key → { canAdd, canEdit, canDelete }. */
export function buildViewPermissionMap(allowedViews) {
  const map = new Map();
  (allowedViews || []).forEach((node) => {
    if (node?.children?.length > 0) return;
    const key = normalizeViewKey(node);
    if (!key) return;
    map.set(key, {
      canAdd:    node.canAdd    !== false,
      canEdit:   node.canEdit   !== false,
      canDelete: node.canDelete !== false,
    });
  });
  return map;
}

/** One view's flags from a permission map; full access when it isn't there. */
export const lookupViewPermissions = (map, viewUrl) =>
  map.get(normalizeViewKey({ url: viewUrl })) || FULL_ACCESS;

const ViewPermissionsContext = createContext(new Map());

export function ViewPermissionsProvider({ allowedViews, children }) {
  const byKey = useMemo(() => buildViewPermissionMap(allowedViews), [allowedViews]);

  return (
    <ViewPermissionsContext.Provider value={byKey}>
      {children}
    </ViewPermissionsContext.Provider>
  );
}

export function useViewPermissions(viewUrl) {
  return lookupViewPermissions(useContext(ViewPermissionsContext), viewUrl);
}
